import React, { useImperativeHandle, useState, useCallback, useMemo, useContext } from 'react';
import { Box } from '../../../Box';
import { useSelectionHelper } from '../../hooks/useSelectionHelper/useSelectionHelper';
import { RowActionType } from '../../Table.decl';
import { ActionBar } from '../ActionBar/ActionBar';
import { NoDataMessage } from '../NoDataMessage/NoDataMessage';
import { Row } from '../Row/Row';
import { TableHeaders } from '../TableHeaders/TableHeaders';
import { TableRootContext } from '../TableRoot/context';
import { StyledTable } from './styled';

var Table = function Table(_ref, ref) {
  var data = _ref.data,
      columns = _ref.columns,
      disableRow = _ref.disableRow,
      noDataMessage = _ref.noDataMessage,
      verticalScrollingParent = _ref.verticalScrollingParent,
      _ref$pageSize = _ref.pageSize,
      pageSize = _ref$pageSize === void 0 ? 50 : _ref$pageSize,
      order = _ref.order,
      rowActions = _ref.rowActions,
      _ref$rowActionsType = _ref.rowActionsType,
      rowActionsType = _ref$rowActionsType === void 0 ? RowActionType.Dropdown : _ref$rowActionsType,
      bulkActions = _ref.bulkActions,
      unselectableRowCondition = _ref.unselectableRowCondition,
      hideActionBar = _ref.hideActionBar,
      onRowClick = _ref.onRowClick,
      onOrderChange = _ref.onOrderChange,
      onSelectionChange = _ref.onSelectionChange;

  var _useContext = useContext(TableRootContext),
      horizontallyScrollableContainerRef = _useContext.horizontallyScrollableContainerRef,
      loading = _useContext.loading,
      recordAttributeIdentifier = _useContext.recordAttributeIdentifier;

  var _useState = useState(),
      actionBarPositionSticky = _useState[0],
      setActionBarPositionSticky = _useState[1];

  var rowsData = useMemo(function () {
    return loading ? new Array(pageSize).fill({}) : data;
  }, [data, loading, pageSize]);
  var rowsSelectable = useMemo(function () {
    return !!(bulkActions && bulkActions.length);
  }, [bulkActions]);

  var _useSelectionHelper = useSelectionHelper(rowsSelectable, rowsData, recordAttributeIdentifier, rowsSelectable ? onSelectionChange : undefined),
      selectedRows = _useSelectionHelper.selectedRows,
      columnSelected = _useSelectionHelper.columnSelected,
      selectedCount = _useSelectionHelper.selectedCount,
      toggleRecord = _useSelectionHelper.toggleRecord,
      toggleAllRecordsFromCurrentPage = _useSelectionHelper.toggleAllRecordsFromCurrentPage,
      clearSelection = _useSelectionHelper.clearSelection;

  useImperativeHandle(ref, function () {
    return {
      clearSelection: clearSelection
    };
  });
  var handleStickyChange = useCallback(function (stuck) {
    setActionBarPositionSticky(stuck);
  }, []);
  var hasActions = typeof rowActions === 'function' || !!(rowActions && rowActions.length);
  var tableHasNoData = !loading && rowsData.length === 0;
  var needSpaceForActionBar = selectedCount && !actionBarPositionSticky && !hideActionBar;
  var tableAdditionalBottomPadding = needSpaceForActionBar || tableHasNoData ? 'xxl' : 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledTable, {
    paddingBottom: tableAdditionalBottomPadding
  }, /*#__PURE__*/React.createElement(TableHeaders, {
    onSelectionChange: toggleAllRecordsFromCurrentPage,
    selectColumnDisabled: !!unselectableRowCondition || !!disableRow,
    selectColumnChecked: !!columnSelected,
    columns: columns,
    onOrderChange: onOrderChange,
    selectable: rowsSelectable,
    hasActions: hasActions && !selectedCount,
    order: order
  }), /*#__PURE__*/React.createElement(Box, {
    as: "tbody",
    display: "table-row-group"
  }, rowsData.map(function (record, index) {
    var _unselectableRowCondi;

    return /*#__PURE__*/React.createElement(Row, {
      key: index,
      data: record,
      columns: columns,
      actions: selectedCount > 0 ? undefined : rowActions,
      actionsType: rowActionsType,
      disabled: typeof disableRow === 'function' ? disableRow(record) : disableRow,
      onClick: onRowClick,
      selectable: rowsSelectable,
      selectDisabled: (_unselectableRowCondi = unselectableRowCondition == null ? void 0 : unselectableRowCondition(record)) != null ? _unselectableRowCondi : false,
      onSelectChange: toggleRecord,
      selected: !!selectedRows[record[recordAttributeIdentifier]]
    });
  })), tableHasNoData && /*#__PURE__*/React.createElement(Box, {
    as: "tfoot",
    display: "table-footer-group"
  }, /*#__PURE__*/React.createElement(NoDataMessage, {
    message: noDataMessage
  }))), selectedCount > 0 && !hideActionBar && /*#__PURE__*/React.createElement(ActionBar, {
    actions: bulkActions,
    nonVerticallyScrollingParent: horizontallyScrollableContainerRef,
    verticalScrollingParent: verticalScrollingParent,
    onStickyChange: handleStickyChange,
    onCancel: clearSelection,
    selectedRows: Object.values(selectedRows)
  }));
};

export var TableInner = /*#__PURE__*/React.forwardRef(Table);